import React, { FC } from 'react';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';

import { AnimationWrapper, ItemWrapper } from './styles';
import Field from '../New/Field';

export const ListWrapper = styled.div`
  z-index: 1;
`;

const getItemStyle = (isDragging, draggableStyle) => {
  const windowWidth = window.innerWidth;
  const drawerWidth = 448;
  const translateXValue = -(windowWidth - drawerWidth);

  return {
    ...draggableStyle,
    /* Custom Styling Explanation:
     * This component is rendered inside a Drawer component that slides in from the right side of the screen.
     * When an item is being dragged, react-beautiful-dnd applies absolute positioning, which by default positions the item
     * as if the Drawer were still closed (i.e., off-screen).
     *
     * To correct this and ensure that the item appears in the correct position while being dragged, we apply a transform
     * based on the window's width minus the drawer's width. This compensates for the Drawer’s slide-in effect, aligning the
     * dragged item with the rest of the content inside the Drawer, so it doesn't appear off-screen during dragging.
     */
    ...(isDragging && {
      transform: draggableStyle.transform
        ? `${draggableStyle.transform} translateX(${translateXValue}px)`
        : `translateX(${translateXValue}px)`,
    }),
    zIndex: isDragging ? 9999 : 'auto',
  };
};

interface Props {
  valueList: any;
  idToRemove: string;
  content: ({ item }: { item: any }) => JSX.Element;
  handleOrderChange: (value: string[]) => void;
}

const DragWrapper: FC<Props> = ({ valueList, idToRemove, content, handleOrderChange }) => {
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const newItemOrder = reorder(valueList.group, result.source.index, result.destination.index);
    if (newItemOrder) {
      handleOrderChange(newItemOrder.map((item: any) => item?.id));
    }
  };

  const Content = content;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="list">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {valueList.group.map((item: any, index: number) => (
              <Draggable draggableId={item.id} index={index} key={item.id}>
                {(provided, snapshot) => (
                  <AnimationWrapper
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    removing={idToRemove == item.id}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
                    {valueList.label ? (
                      <Field label={valueList.label}>
                        <Content item={item} />
                      </Field>
                    ) : (
                      <ItemWrapper>
                        <Content item={item} />
                      </ItemWrapper>
                    )}
                  </AnimationWrapper>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DragWrapper;
